import { createFileRoute } from "@tanstack/react-router"
import { DialogTrigger } from "react-aria-components"

import { CodeBlock } from "@/components/dev/CodeBlock"
import { PropsTable } from "@/components/dev/PropsTable"
import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import Modal from "@/globalComponents/molecules/Modal"

export const Route = createFileRoute("/qr/$qrLookupID/dev/molecules/modal")({
  staticData: {
    pageName: "Modal Component",
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <>
      <style>{`
        .dev-container > *:not(:first-child) {
          margin-top: var(--space-5);
        }
      `}</style>
      <div className="dev-container">
        <Card>
          <Heading level={1} as={2}>
            Modal Component
          </Heading>
          <Text>
            The Modal component provides accessible dialog overlays built on
            React Aria Components. It includes support for both centered modals
            and drawer-style modals with header, body, and footer sections.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Basic Centered Modal
          </Heading>
          <Text>
            The default modal style appears centered on the screen with an
            overlay backdrop.
          </Text>
          <DialogTrigger>
            <Button buttonType="primary">Open Centered Modal</Button>
            <Modal>
              <Modal.Header>
                <Heading level={2} as={3}>
                  Modal Title
                </Heading>
              </Modal.Header>
              <Modal.Body>
                <Text>
                  This is a basic centered modal. The header includes a close
                  button by default, and clicking outside the modal or pressing
                  Escape will close it.
                </Text>
                <Text>
                  Modals are built on React Aria Components for full keyboard
                  navigation and screen reader support.
                </Text>
              </Modal.Body>
              <Modal.Footer>
                {(state) => (
                  <>
                    <Button
                      buttonType="default"
                      presentation="ghost"
                      onPress={state.close}
                    >
                      Cancel
                    </Button>
                    <Button buttonType="primary" onPress={state.close}>
                      Confirm
                    </Button>
                  </>
                )}
              </Modal.Footer>
            </Modal>
          </DialogTrigger>
          <CodeBlock>{`<DialogTrigger>
  <Button buttonType="primary">Open Centered Modal</Button>
  <Modal>
    <Modal.Header>
      <Heading level={2} as={3}>Modal Title</Heading>
    </Modal.Header>
    <Modal.Body>
      <Text>Modal content goes here...</Text>
    </Modal.Body>
    <Modal.Footer>
      {(state) => (
        <>
          <Button
            buttonType="default"
            presentation="ghost"
            onPress={state.close}
          >
            Cancel
          </Button>
          <Button buttonType="primary" onPress={state.close}>
            Confirm
          </Button>
        </>
      )}
    </Modal.Footer>
  </Modal>
</DialogTrigger>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Drawer Modal
          </Heading>
          <Text>
            Set <code>modalStyle="drawer"</code> for a modal that slides up from
            the bottom of the screen (mobile-friendly).
          </Text>
          <DialogTrigger>
            <Button buttonType="primary">Open Drawer Modal</Button>
            <Modal modalStyle="drawer">
              <Modal.Header>
                <Heading level={2} as={3}>
                  Drawer Modal
                </Heading>
              </Modal.Header>
              <Modal.Body>
                <Text>
                  This drawer-style modal slides up from the bottom on mobile
                  devices and appears as a centered modal on larger screens.
                </Text>
                <Text>
                  This pattern is great for mobile-first experiences and
                  progressive disclosure.
                </Text>
              </Modal.Body>
              <Modal.Footer>
                {(state) => (
                  <Button
                    buttonType="primary"
                    onPress={state.close}
                    style={{ width: "100%" }}
                  >
                    Got it
                  </Button>
                )}
              </Modal.Footer>
            </Modal>
          </DialogTrigger>
          <CodeBlock>{`<DialogTrigger>
  <Button buttonType="primary">Open Drawer Modal</Button>
  <Modal modalStyle="drawer">
    <Modal.Header>
      <Heading level={2} as={3}>Drawer Modal</Heading>
    </Modal.Header>
    <Modal.Body>
      <Text>Drawer content...</Text>
    </Modal.Body>
    <Modal.Footer>
      {(state) => (
        <Button
          buttonType="primary"
          onPress={state.close}
          style={{ width: "100%" }}
        >
          Got it
        </Button>
      )}
    </Modal.Footer>
  </Modal>
</DialogTrigger>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Modal without Close Button
          </Heading>
          <Text>
            Use <code>hideClose</code> prop on Modal.Header to hide the close
            button for modals that require explicit user action.
          </Text>
          <DialogTrigger>
            <Button buttonType="primary">Open Required Action Modal</Button>
            <Modal isDismissable={false}>
              <Modal.Header hideClose>
                <Heading level={2} as={3}>
                  Important Notice
                </Heading>
              </Modal.Header>
              <Modal.Body>
                <Text>
                  This modal requires an explicit action. There's no close
                  button in the header, and clicking outside won't dismiss it.
                </Text>
                <Text level={3}>
                  Note: <code>isDismissable=&#123;false&#125;</code> prevents
                  closing via overlay click or Escape key.
                </Text>
              </Modal.Body>
              <Modal.Footer>
                {(state) => (
                  <Button
                    buttonType="primary"
                    onPress={state.close}
                    style={{ width: "100%" }}
                  >
                    I Understand
                  </Button>
                )}
              </Modal.Footer>
            </Modal>
          </DialogTrigger>
          <CodeBlock>{`<DialogTrigger>
  <Button buttonType="primary">Open Required Action Modal</Button>
  <Modal isDismissable={false}>
    <Modal.Header hideClose>
      <Heading level={2} as={3}>Important Notice</Heading>
    </Modal.Header>
    <Modal.Body>
      <Text>This modal requires an explicit action...</Text>
    </Modal.Body>
    <Modal.Footer>
      {(state) => (
        <Button buttonType="primary" onPress={state.close}>
          I Understand
        </Button>
      )}
    </Modal.Footer>
  </Modal>
</DialogTrigger>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Vertical Footer Arrangement
          </Heading>
          <Text>
            Use <code>arrangement="vertical"</code> on Modal.Footer to stack
            buttons vertically instead of horizontally.
          </Text>
          <DialogTrigger>
            <Button buttonType="primary">Open Vertical Footer Modal</Button>
            <Modal>
              <Modal.Header>
                <Heading level={2} as={3}>
                  Confirm Action
                </Heading>
              </Modal.Header>
              <Modal.Body>
                <Text>
                  This modal has a vertical footer arrangement, which is useful
                  for mobile layouts or when you have multiple action buttons.
                </Text>
              </Modal.Body>
              <Modal.Footer arrangement="vertical">
                {(state) => (
                  <>
                    <Button
                      buttonType="primary"
                      onPress={state.close}
                      style={{ width: "100%" }}
                    >
                      Primary Action
                    </Button>
                    <Button
                      buttonType="default"
                      onPress={state.close}
                      style={{ width: "100%" }}
                    >
                      Secondary Action
                    </Button>
                    <Button
                      buttonType="default"
                      presentation="ghost"
                      onPress={state.close}
                      style={{ width: "100%" }}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Modal.Footer>
            </Modal>
          </DialogTrigger>
          <CodeBlock>{`<DialogTrigger>
  <Button buttonType="primary">Open Vertical Footer Modal</Button>
  <Modal>
    <Modal.Header>
      <Heading level={2} as={3}>Confirm Action</Heading>
    </Modal.Header>
    <Modal.Body>
      <Text>Modal content...</Text>
    </Modal.Body>
    <Modal.Footer arrangement="vertical">
      {(state) => (
        <>
          <Button buttonType="primary" onPress={state.close}>
            Primary Action
          </Button>
          <Button buttonType="default" onPress={state.close}>
            Secondary Action
          </Button>
          <Button
            buttonType="default"
            presentation="ghost"
            onPress={state.close}
          >
            Cancel
          </Button>
        </>
      )}
    </Modal.Footer>
  </Modal>
</DialogTrigger>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Danger Modal
          </Heading>
          <Text>
            Example of a destructive action modal using danger button styling.
          </Text>
          <DialogTrigger>
            <Button buttonType="danger">Delete Account</Button>
            <Modal>
              <Modal.Header>
                <Heading level={2} as={3}>
                  Delete Account?
                </Heading>
              </Modal.Header>
              <Modal.Body>
                <Text>
                  Are you sure you want to delete your account? This action
                  cannot be undone.
                </Text>
                <Text level={3}>
                  All your data, including parking history and saved vehicles,
                  will be permanently removed.
                </Text>
              </Modal.Body>
              <Modal.Footer>
                {(state) => (
                  <>
                    <Button
                      buttonType="default"
                      presentation="ghost"
                      onPress={state.close}
                    >
                      Cancel
                    </Button>
                    <Button
                      buttonType="danger"
                      onPress={() => {
                        alert("Account deleted")
                        state.close()
                      }}
                    >
                      Delete Account
                    </Button>
                  </>
                )}
              </Modal.Footer>
            </Modal>
          </DialogTrigger>
          <CodeBlock>{`<DialogTrigger>
  <Button buttonType="danger">Delete Account</Button>
  <Modal>
    <Modal.Header>
      <Heading level={2} as={3}>Delete Account?</Heading>
    </Modal.Header>
    <Modal.Body>
      <Text>
        Are you sure you want to delete your account? This action
        cannot be undone.
      </Text>
    </Modal.Body>
    <Modal.Footer>
      {(state) => (
        <>
          <Button
            buttonType="default"
            presentation="ghost"
            onPress={state.close}
          >
            Cancel
          </Button>
          <Button
            buttonType="danger"
            onPress={() => {
              // Perform delete action
              state.close()
            }}
          >
            Delete Account
          </Button>
        </>
      )}
    </Modal.Footer>
  </Modal>
</DialogTrigger>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Simple Informational Modal
          </Heading>
          <Text>
            Minimal modal with just a message and single action button.
          </Text>
          <DialogTrigger>
            <Button buttonType="primary" presentation="ghost">
              View Info
            </Button>
            <Modal>
              <Modal.Header>
                <Heading level={2} as={3}>
                  Parking Pass Activated
                </Heading>
              </Modal.Header>
              <Modal.Body>
                <Text>
                  Your monthly parking pass has been activated and is now valid
                  for all participating locations.
                </Text>
              </Modal.Body>
              <Modal.Footer>
                {(state) => (
                  <Button
                    buttonType="primary"
                    onPress={state.close}
                    style={{ width: "100%" }}
                  >
                    Got it
                  </Button>
                )}
              </Modal.Footer>
            </Modal>
          </DialogTrigger>
          <CodeBlock>{`<DialogTrigger>
  <Button buttonType="primary" presentation="ghost">
    View Info
  </Button>
  <Modal>
    <Modal.Header>
      <Heading level={2} as={3}>Parking Pass Activated</Heading>
    </Modal.Header>
    <Modal.Body>
      <Text>Your monthly parking pass has been activated...</Text>
    </Modal.Body>
    <Modal.Footer>
      {(state) => (
        <Button buttonType="primary" onPress={state.close}>
          Got it
        </Button>
      )}
    </Modal.Footer>
  </Modal>
</DialogTrigger>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Modal Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "modalStyle",
                type: '"centered" | "drawer"',
                default: '"centered"',
                description:
                  "Visual style - centered appears in the middle, drawer slides from bottom",
              },
              {
                name: "isDismissable",
                type: "boolean",
                default: "true",
                description:
                  "Whether the modal can be closed by clicking overlay or pressing Escape",
              },
              {
                name: "isKeyboardDismissDisabled",
                type: "boolean",
                default: "false",
                description: "Disables closing with Escape key",
              },
              {
                name: "children",
                type: "ReactNode",
                default: "Required",
                description: "Modal content (Header, Body, Footer)",
              },
            ]}
          />
          <Text>
            Note: Modal extends React Aria ModalOverlayProps, so it supports all
            standard overlay props including focus management and animations.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Modal.Header Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "hideClose",
                type: "boolean",
                default: "false",
                description: "Hides the close button in the header",
              },
              {
                name: "className",
                type: "string",
                default: "undefined",
                description: "Additional CSS classes",
              },
              {
                name: "children",
                type: "ReactNode",
                default: "undefined",
                description: "Header content (typically a Heading)",
              },
            ]}
          />
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Modal.Footer Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "arrangement",
                type: '"horizontal" | "vertical"',
                default: '"horizontal"',
                description: "How to arrange footer buttons",
              },
              {
                name: "children",
                type: "(state: OverlayTriggerState) => ReactNode",
                default: "Required",
                description:
                  "Render function that receives modal state (with close method)",
              },
            ]}
          />
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Usage with DialogTrigger
          </Heading>
          <Text>
            The Modal component must be wrapped in a React Aria{" "}
            <code>DialogTrigger</code> to manage its open/close state. The first
            child of DialogTrigger is the trigger button, and the second is the
            Modal.
          </Text>
          <Text>
            The Modal.Footer's children function receives an{" "}
            <code>OverlayTriggerState</code> object with methods like{" "}
            <code>close()</code>, <code>open()</code>, and <code>isOpen</code>{" "}
            to control the modal state.
          </Text>
          <CodeBlock>{`import { DialogTrigger } from "react-aria-components"

<DialogTrigger>
  {/* Trigger button */}
  <Button>Open Modal</Button>

  {/* Modal */}
  <Modal>
    <Modal.Header>...</Modal.Header>
    <Modal.Body>...</Modal.Body>
    <Modal.Footer>
      {(state) => (
        <Button onPress={state.close}>Close</Button>
      )}
    </Modal.Footer>
  </Modal>
</DialogTrigger>`}</CodeBlock>
        </Card>
      </div>
    </>
  )
}
